import { useEffect, useState } from "react";
import { api } from "../api/client";
import { theme, card } from "../theme";

interface Props {
  /** API endpoint to fetch from */
  endpoint: string;
  /** Called when the tile is clicked */
  onClick: () => void;
}

export function ActionsTile({ endpoint, onClick }: Props) {
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ total: number }>(endpoint)
      .then((d) => setTotal(d.total))
      .catch(() => setTotal(null))
      .finally(() => setLoading(false));
  }, [endpoint]);

  const hasActions = total !== null && total > 0;

  return (
    <button
      onClick={onClick}
      style={{
        ...card,
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        border: hasActions
          ? `1px solid var(--c-warning)`
          : "1px solid var(--c-line)",
        background: hasActions ? "var(--c-warningBg)" : "var(--c-panel)",
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "16px 20px",
        transition: "box-shadow 0.15s, border-color 0.15s",
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = "var(--c-sheetShadow)"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = "none"; }}
    >
      {/* Icon */}
      <div style={{
        width: 44,
        height: 44,
        borderRadius: 3,
        background: hasActions ? "var(--c-warning)" : "var(--c-border)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke={hasActions ? "#fff" : "var(--c-muted)"} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 5h12M4 10h12M4 15h7" />
          <circle cx="16" cy="15" r="3" fill={hasActions ? "#fff" : "var(--c-muted)"} stroke="none" />
          <path d="M16 13.5v1.5l1 1" stroke={hasActions ? "var(--c-warning)" : "var(--c-panel)"} strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: hasActions ? "var(--c-warningText)" : theme.color.heading,
          marginBottom: 2,
        }}>
          My Actions
        </div>
        <div style={{ fontSize: 12, color: hasActions ? "var(--c-warningText)" : theme.color.subtle }}>
          {loading
            ? "Loading…"
            : total === null
            ? "Could not load"
            : total === 0
            ? "Nothing to do right now"
            : `${total} item${total !== 1 ? "s" : ""} need${total === 1 ? "s" : ""} your attention`}
        </div>
      </div>

      {/* Badge */}
      {hasActions && (
        <div style={{
          background: "var(--c-warning)",
          color: "#fff",
          borderRadius: 3,
          fontWeight: 800,
          fontSize: 14,
          minWidth: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 8px",
          flexShrink: 0,
        }}>
          {total}
        </div>
      )}

      {/* Chevron */}
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={hasActions ? "var(--c-warningText)" : theme.color.muted} strokeWidth="1.6" strokeLinecap="round">
        <path d="M5 3l4 4-4 4" />
      </svg>
    </button>
  );
}
